import { createAsyncThunk } from '@reduxjs/toolkit';

import { driverAPI } from '@/modules/driver/api';
import { TUpdateOwnDriverInfoForm } from '@/modules/driver/model/driver';
import { namespace } from '@/modules/driver/namespace';
import { getCurrentDriver } from '@/modules/driver/store/selectors';
import type { RootState } from '@/root-store';
import { ThunkResult } from '@/shared/actions/thunk-result';
import { ISubmitFormResult } from '@/shared/models/formSubmit';

import { updateDriver } from '../actions';

let lastRequestId: string | undefined;

const updateDriverDetails = createAsyncThunk<
  ThunkResult<ISubmitFormResult<TUpdateOwnDriverInfoForm>>,
  Partial<TUpdateOwnDriverInfoForm>
>(
  `${namespace}/submit-update-driver-details`,
  async (formValues, { dispatch, requestId, getState }) => {
    try {
      if (lastRequestId !== undefined) {
        return { isAlreadyExecuted: true };
      }

      lastRequestId = requestId;

      const driver = getCurrentDriver(getState() as RootState);

      if (!driver) {
        return { isSucceed: false };
      }

      const [, driverUpdateErrors] = await driverAPI.updateDriver(driver.id, {
        ...driver,
        ...formValues,
      });

      if (driverUpdateErrors) {
        const { licensingAuthority, licenseNumber, homeAddress, ...other } = driverUpdateErrors;
        const result: ISubmitFormResult<TUpdateOwnDriverInfoForm> = {};

        if (licensingAuthority || licenseNumber || homeAddress) {
          result.validationErrors = { licensingAuthority, licenseNumber, homeAddress };
        }

        const baseErrors = Object.values(other).flat();

        if (baseErrors.length > 0) {
          result.baseErrors = baseErrors;
        }

        return { isSucceed: false, result };
      }

      dispatch(updateDriver({ id: driver.id, update: formValues }));

      return { isSucceed: true };
    } finally {
      lastRequestId = undefined;
    }
  },
);

export { updateDriverDetails };
